import { createGlobalStyle, css } from "styled-components";
import type {
  AppTheme,
  ContainerWidth,
  ContainerPadding,
  CustomWidth,
  BreakpointQueries,
  SvgSafeFillMixinFunction,
  MotifType,
} from "./layout.types";

import {
  baseResetCSSWrapper,
  baseTextCSSWrapper,
  baseGridCSSWrapper,
  baseFormCSSWrapper,
  baseSupplementalCSSWrapper,
} from "./layoutCSSWrapper.mjs";
import { light } from "styles/palette";

export const GlobalStyle = createGlobalStyle`
  html,
  body {
    width: 100%;
    min-height: 100vh;


    &.lock-scroll {
      height: 100vh;
      max-height: 100vh;
    }
  }

  .grecaptcha-badge {
    visibility: hidden;
  }

  .videoPlayerModal {
    header {
      display: none;
    }

    video {
      border-radius: 8px;
    }
  }
  
  .react-modal-portal-overlay {
    z-index: 100000;
  }

  .lock-scroll {
    overflow: hidden !important;
    scrollbar-gutter: stable;
    touch-action: none;
    -webkit-overflow-scrolling: touch;
  }

  ::selection {
    background: rgba(0, 0, 0, 0.3);
    color: #ffffff;
  }

  ::-moz-selection {
    background: rgba(0, 0, 0, 0.3);
    color: #ffffff;
  }
  html *,
  *:focus,
  *
  {
      outline: none !important;
  }
  button {
      overflow: visible;
  }
  button,
  input
  {
      margin: 0;
      padding: 0;
      width: auto;
  }
  button::-moz-focus-inner,
  input::-moz-focus-inner
  {
      border: 0;
      padding: 0;
  }

  input[type="number"]::-webkit-inner-spin-button,
  input[type="number"]::-webkit-outer-spin-button,

  input[type="date"]::-webkit-inner-spin-button,
  input[type="date"]::-webkit-outer-spin-button,
  input[type="date"]::-webkit-calendar-picker-indicator
  {
      -webkit-appearance: none;
      margin: 0;
      display: none;
  }
  input[type="number"],
  input[type="date"]
  {
    -webkit-appearance: none;
      -moz-appearance: textfield;
  }

  a {
      color: $primary-link-color;
      [href^="tel:"] {
          color: $primary-text-color;
      }
  }
  input,
  textarea,
  select,
  button
  {
      -webkit-appearance: none;
      border-radius: 0;
  }

  b, strong {
      font-weight: 600;
  }

  .list-unstyled {
      margin: 0;
  }

  *, :before, :after {
      background-repeat: no-repeat;
      background-position: 50% 50%;
  }

  html, body {
    font-family: ${(props) => props.theme.fontFamily};

    -webkit-font-smoothing: antialiased;
    font-synthesis: none;
    font-variant-ligatures: no-common-ligatures;
  }

  html {
    background-color: ${(props) => props.theme.backgroundColor};
    height: 100%;
    min-height: 100%;
    position: relative;
  }

  body {
    height: 100%;
    max-width: 100%;
    min-height: 100%;
    background-color: transparent;
  }

  a {
    cursor: pointer;
    text-decoration: none;
  }

  hr {
    border: none;
    border-top: 1px solid #e8e8e8;
    border-left: none;
    border-right: none;
    margin-top: 40px;
    margin-bottom: 35px;
  }

  #root {
    min-height: 100%;
  }

  .container-fluid {
    max-width: 1200px;
  }

  .app-container {
    margin-right: auto;
    margin-left: auto;
    padding-left: 15px;
    padding-right: 15px;
    overflow: visible;
    position: relative;
    max-width: 1390px;
  }

  .fade-in {
    ${(props) => props.theme.TransitionDefault}
    transform: translate3d(0px, -20px, 0px);
    transition-delay: 0s;
    opacity: 0;
		will-change: transform, opacity;
    &.idx-01 {
      transition-delay: 0.2s;
    }
    &.idx-02 {
      transition-delay: 0.4s;
    }
    &.idx-03 {
      transition-delay: 0.6s;
    }
    &.idx-04 {
      transition-delay: 0.8s;
    }
    &.idx-05 {
      transition-delay: 1s;
    }
    &.idx-06 {
      transition-delay: 1.2s;
    }
    &.idx-07 {
      transition-delay: 1.4s;
    }
    &.idx-08 {
      transition-delay: 1.6s;
    }

    .in-view & {
      transform: translate3d(0px, 0px, 0px);
      opacity: 1;
    }
  }

  ${baseResetCSSWrapper};
  ${baseTextCSSWrapper};
  ${baseGridCSSWrapper};
  ${baseFormCSSWrapper};
  ${baseSupplementalCSSWrapper};

  .ReactModalPortal .modal-overlay {
    backdrop-filter: blur(10px);
    z-index: 100000;
  }
`;

// End of global style

export const title_defaults = css`
  font-size: 32px;
  color: #4a4a4a;
  line-height: 1.4;
  text-align: center;

  @media (max-width: ${(props) => props.theme.Breakingpoint.md}px) {
    font-size: 27px;
  }

  @media (max-width: ${(props) => props.theme.Breakingpoint.sm}px) {
    font-size: 24px;
    padding-top: 5px;
    margin-bottom: 5px;
  }
`;

export const TitleDefault = css`
  ${title_defaults}
`;

export const description_defaults = css`
  font-size: 16px;
  color: #939393;
  font-weight: 400;

  text-align: center;
  line-height: ${22.4 / 16};
  @media (max-width: ${(props) => props.theme.Breakingpoint.md}px) {
    font-size: 18px;
  }
  @media (max-width: ${(props) => props.theme.Breakingpoint.sm}px) {
    font-size: 16px;
  }
`;

export const DescriptionDefault = css`
  ${description_defaults}
`;

export const header_defaults = css`
  padding-left: 15px;
  padding-right: 15px;
`;

export const HeaderDefault = css`
  ${header_defaults}
`;

export const GlideInDefault = css`
  transition:
    opacity 0.2s cubic-bezier(0.26, 0.46, 0.45, 0.95) 0s,
    transform 0.2s cubic-bezier(0.26, 0.46, 0.45, 0.95) 0s;
  opacity: 0;
  .in-view & {
    opacity: 1;
  }
`;

export const TransitionDefault = css`
  transition: all 0.9s cubic-bezier(0.26, 0.46, 0.45, 0.95) 0s;
`;

export const Transition = (type, duration, delay) => {
  return css`
    transition: ${type ? type : "all"} ${duration ? duration : 0.6}s
      cubic-bezier(0.26, 0.46, 0.45, 0.95) ${delay ? delay : 0}s;
  `;
};

export const landing_title_defaults = css`
  font-size: 36px;
  color: #101010;
  line-height: 1;
  text-align: center;

  font-weight: 800;
  margin-bottom: 10px;
  @media (max-width: ${(props) => props.theme.break.sm}px) {
    font-size: 32px;
  }
`;

export const LandingTitleDefault = css`
  ${(props) => props.theme.GlideInDefault}
  ${landing_title_defaults}
`;

export const theme: AppTheme = {
  fontFamily: `"UI Sans", "Lato", sans-serif`,
  backgroundColor: "#ffffff",
  Transition,
  TransitionDefault,
  HeaderDefault,
  TitleDefault,
  DescriptionDefault,
  LandingTitleDefault,
  GlideInDefault,
  Breakingpoint: {
    xs: 480,
    sm: 767,
    md: 992,
    lg: 1200,
  },
  break: {
    xs: 480,
    sm: 767,
    md: 992,
    lg: 1200,
  },
  colors: light,
  font: {
    size10: "10px",
    size11: "11px",
    size12: "12px",
    size13: "13px",
    size14: "14px",
    size16: "16px",
    size18: "18px",
    size20: "20px",
    size24: "24px",
    size32: "32px",
    size36: "36px",
    size40: "40px",
    size48: "48px",
  },
  zIndex: {
    appSection: 888,
  },
};

// #############################################################################
// Constants
// #############################################################################
export const DEFAULT_MOTIF: MotifType = "dark";

export const CUSTOM_WIDTH: CustomWidth = {
  TOP_NAV_MOBILE: 991,
};

export const CONTAINER_WIDTH: ContainerWidth = {
  MOBILE: 460,
  TABLET: 768,
  DESKTOP: 1096,
};

export const CONTAINER_PADDING: ContainerPadding = {
  MOBILE: 16,
  TABLET: 32,
  DESKTOP: 32,
};

export const BREAKPOINTS: BreakpointQueries = {
  SMALL_MOBILE_AND_DOWN: `(max-width: 376px)`,
  MOBILE_AND_DOWN: `(max-width: ${
    CONTAINER_WIDTH.MOBILE + CONTAINER_PADDING.MOBILE * 2
  }px)`,
  TABLET_AND_DOWN: `(max-width: ${
    CONTAINER_WIDTH.TABLET + CONTAINER_PADDING.TABLET * 2
  }px)`,
  DESKTOP_AND_DOWN: `(max-width: ${
    CONTAINER_WIDTH.DESKTOP + CONTAINER_PADDING.DESKTOP * 2
  }px)`,
  TOP_NAV_MOBILE_AND_DOWN: `(max-width: ${CUSTOM_WIDTH.TOP_NAV_MOBILE}px)`,
  TOP_NAV_MOBILE_UP: `(min-width: ${CUSTOM_WIDTH.TOP_NAV_MOBILE + 1}px)`,
};

// #############################################################################
// Mixins
// #############################################################################
export const SvgSafeFillMixin: SvgSafeFillMixinFunction = (
  color,
  important = false,
) => css`
  svg {
    /* More safe would be to specify "> path, > ellipse" etc */
    > *:not(mask) {
      fill: ${color}${important ? " !important" : ""};
    }
  }
`;
